/**
 * Lightweight, dependency-free validators for phone, WhatsApp number and
 * WhatsApp wa.me URL. Returns user-facing error messages or null when valid.
 *
 * Rules (deliberately strict but not country-locked):
 * - International phone: starts with "+", 8–15 digits after the "+",
 *   only digits/spaces/dashes/parentheses allowed.
 * - Display phone: at least 5 digits, only digits/spaces/dashes/parentheses/+.
 * - WhatsApp number: same as international phone.
 * - WhatsApp URL: must be https://wa.me/<digits> with the digits matching
 *   the WhatsApp number (when both are provided).
 */

const INTL_RE = /^\+[\d\s().-]{7,24}$/;
const DISPLAY_RE = /^[+\d\s().-]{5,24}$/;
const WA_URL_RE = /^https:\/\/wa\.me\/(\d{8,15})(?:[/?#].*)?$/i;

const digitsOnly = (s: string) => s.replace(/\D+/g, "");

export const validateInternationalPhone = (raw: string): string | null => {
  const v = raw.trim();
  if (!v) return "Phone is required";
  if (!v.startsWith("+")) return "Must start with + and country code (e.g. +44…)";
  if (!INTL_RE.test(v)) return "Use only +, digits, spaces, dashes or parentheses";
  const d = digitsOnly(v);
  if (d.length < 8) return "Too short — need at least 8 digits";
  if (d.length > 15) return "Too long — max 15 digits (E.164)";
  return null;
};

export const validateDisplayPhone = (raw: string): string | null => {
  const v = raw.trim();
  if (!v) return "Display phone is required";
  if (!DISPLAY_RE.test(v)) return "Use only digits, spaces, dashes, parentheses or +";
  if (digitsOnly(v).length < 5) return "Too short — need at least 5 digits";
  return null;
};

export const validateWhatsAppNumber = (raw: string): string | null => {
  const v = raw.trim();
  if (!v) return null; // optional
  if (!v.startsWith("+")) return "Must start with + and country code (e.g. +44…)";
  if (!INTL_RE.test(v)) return "Use only +, digits, spaces, dashes or parentheses";
  const d = digitsOnly(v);
  if (d.length < 8 || d.length > 15) return "Need 8–15 digits";
  return null;
};

export const validateWhatsAppUrl = (raw: string, waNumber?: string): string | null => {
  const v = raw.trim();
  if (!v) return null; // optional
  const m = WA_URL_RE.exec(v);
  if (!m) return "Must look like https://wa.me/447537103040";
  if (waNumber && waNumber.trim()) {
    const numDigits = digitsOnly(waNumber);
    if (numDigits && m[1] !== numDigits) {
      return "Digits don't match the WhatsApp number above";
    }
  }
  return null;
};

export interface ContactValidation {
  phone_international: string | null;
  phone_display: string | null;
  whatsapp_number: string | null;
  whatsapp_url: string | null;
  hasError: boolean;
  errorCount: number;
}

export const validateContactFields = (input: {
  phone_international: string;
  phone_display: string;
  whatsapp_number: string;
  whatsapp_url: string;
}): ContactValidation => {
  const result = {
    phone_international: validateInternationalPhone(input.phone_international),
    phone_display: validateDisplayPhone(input.phone_display),
    whatsapp_number: validateWhatsAppNumber(input.whatsapp_number),
    whatsapp_url: validateWhatsAppUrl(input.whatsapp_url, input.whatsapp_number),
  };
  const errors = Object.values(result).filter(Boolean) as string[];
  return {
    ...result,
    hasError: errors.length > 0,
    errorCount: errors.length,
  };
};
